import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token (if needed in future)
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token here if needed
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Clients API
export const clientsAPI = {
  getAll: (params) => apiClient.get('/clients', { params }),
  getById: (id) => apiClient.get(`/clients/${id}`),
  create: (data) => apiClient.post('/clients', data),
  update: (id, data) => apiClient.put(`/clients/${id}`, data),
  delete: (id) => apiClient.delete(`/clients/${id}`),
  linkClient: (id, relatedClientId) => apiClient.post(`/clients/${id}/link`, { relatedClientId }),
  unlinkClient: (id, relatedClientId) => apiClient.delete(`/clients/${id}/link/${relatedClientId}`),
  search: (query) => apiClient.get('/clients/search', { params: { q: query } }),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => apiClient.get('/tasks', { params }),
  getById: (id) => apiClient.get(`/tasks/${id}`),
  create: (data) => apiClient.post('/tasks', data),
  update: (id, data) => apiClient.put(`/tasks/${id}`, data),
  delete: (id) => apiClient.delete(`/tasks/${id}`),
  archive: (id) => apiClient.put(`/tasks/${id}/archive`),
  unarchive: (id) => apiClient.put(`/tasks/${id}/unarchive`),
};

// Task Stages API
export const taskStagesAPI = {
  getAll: () => apiClient.get('/task-stages'),
  create: (data) => apiClient.post('/task-stages', data),
  update: (id, data) => apiClient.put(`/task-stages/${id}`, data),
  delete: (id) => apiClient.delete(`/task-stages/${id}`),
  reorder: (stages) => apiClient.put('/task-stages/reorder', stages),
};

// Custom Fields API
export const customFieldsAPI = {
  getAll: () => apiClient.get('/custom-fields'),
  create: (data) => apiClient.post('/custom-fields', data),
  update: (id, data) => apiClient.put(`/custom-fields/${id}`, data),
  delete: (id) => apiClient.delete(`/custom-fields/${id}`),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => apiClient.get('/documents', { params }),
  create: (data) => apiClient.post('/documents', data),
  delete: (id) => apiClient.delete(`/documents/${id}`),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => apiClient.get('/invoices', { params }),
  getById: (id) => apiClient.get(`/invoices/${id}`),
  getAccountsReceivable: () => apiClient.get('/invoices/accounts-receivable'),
  getNextNumber: () => apiClient.get('/invoices/next-number'),
  create: (data) => apiClient.post('/invoices', data),
  update: (id, data) => apiClient.put(`/invoices/${id}`, data),
  pay: (id, paymentMode, paidDate, paymentMemo, paymentAmount) => apiClient.put(`/invoices/${id}/pay`, { paymentMode, paidDate, paymentMemo, paymentAmount }),
  unpay: (id) => apiClient.put(`/invoices/${id}/unpay`),
  delete: (id) => apiClient.delete(`/invoices/${id}`),
  export: (params) => apiClient.get('/invoices/export', { params, responseType: 'blob' }),
};

// Services API
export const servicesAPI = {
  getAll: () => apiClient.get('/services'),
  create: (data) => apiClient.post('/services', data),
  update: (id, data) => apiClient.put(`/services/${id}`, data),
  delete: (id) => apiClient.delete(`/services/${id}`),
};

// Notes API
export const notesAPI = {
  getAll: (clientId) => apiClient.get('/notes', { params: { client_id: clientId } }),
  create: (data) => apiClient.post('/notes', data),
  delete: (id) => apiClient.delete(`/notes/${id}`),
  togglePin: (id) => apiClient.put(`/notes/${id}/pin`),
};

// Team API
export const teamAPI = {
  getMembers: () => apiClient.get('/team/members'),
  createMember: (data) => apiClient.post('/team/members', data),
  updateMember: (id, data) => apiClient.put(`/team/members/${id}`, data),
  addUser: (data) => apiClient.post('/team/add-user', data),
  invite: (data) => apiClient.post('/team/invite', data),
  getInvitations: () => apiClient.get('/team/invitations'),
  removeMember: (id) => apiClient.delete(`/team/members/${id}`),
  login: (data) => apiClient.post('/team/login', data),
  changePassword: (data) => apiClient.post('/team/change-password', data),
  updateProfile: (data) => apiClient.put('/team/profile', data),
};

// Team Chat API
export const teamChatAPI = {
  // Messages
  getMessages: (limit = 50, channelId = null) => apiClient.get('/team-chat/messages', { 
    params: { limit, channel_id: channelId } 
  }),
  sendMessage: (message, channelId = null) => apiClient.post('/team-chat/messages', { 
    message, 
    channelId 
  }),
  deleteMessage: (id) => apiClient.delete(`/team-chat/messages/${id}`),
  // Channels
  getChannels: () => apiClient.get('/team-chat/channels'),
  createChannel: (data) => apiClient.post('/team-chat/channels', data),
  updateChannel: (id, data) => apiClient.put(`/team-chat/channels/${id}`, data),
  deleteChannel: (id) => apiClient.delete(`/team-chat/channels/${id}`),
};

// Pipelines API
export const pipelinesAPI = {
  getAll: () => apiClient.get('/pipelines'),
  getById: (id) => apiClient.get(`/pipelines/${id}`),
  create: (data) => apiClient.post('/pipelines', data),
  update: (id, data) => apiClient.put(`/pipelines/${id}`, data),
  delete: (id) => apiClient.delete(`/pipelines/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => apiClient.get('/dashboard/stats'),
};

// Tags API
export const tagsAPI = {
  getAll: () => apiClient.get('/tags'),
  create: (data) => apiClient.post('/tags', data),
  update: (id, data) => apiClient.put(`/tags/${id}`, data),
  delete: (id) => apiClient.delete(`/tags/${id}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => apiClient.get('/notifications', { params }),
  markAsRead: (id) => apiClient.put(`/notifications/${id}/read`),
  markAsUnread: (id) => apiClient.put(`/notifications/${id}/unread`),
  markAllAsRead: () => apiClient.put('/notifications/mark-all-read'),
  delete: (id) => apiClient.delete(`/notifications/${id}`),
};

// Messages API
export const messagesAPI = {
  getAll: (clientId) => apiClient.get('/messages', { params: { client_id: clientId } }),
  create: (data) => apiClient.post('/messages', data),
  delete: (id) => apiClient.delete(`/messages/${id}`),
};

// Client Pipelines API
export const clientPipelinesAPI = {
  getAll: (params) => apiClient.get('/client-pipelines', { params }),
  create: (data) => apiClient.post('/client-pipelines', data),
  update: (id, data) => apiClient.put(`/client-pipelines/${id}`, data),
  delete: (id) => apiClient.delete(`/client-pipelines/${id}`),
  completeTask: (id, data) => apiClient.post(`/client-pipelines/${id}/complete-task`, data),
  uncompleteTask: (id, data) => apiClient.post(`/client-pipelines/${id}/uncomplete-task`, data),
};

// Client Passwords API (encrypted storage)
export const clientPasswordsAPI = {
  getAll: (clientId) => apiClient.get('/client-passwords', { params: { client_id: clientId } }),
  getDecrypted: (id) => apiClient.get(`/client-passwords/${id}`),
  reveal: (clientId, id) => apiClient.get(`/client-passwords/${id}`),
  create: (clientId, data) => apiClient.post('/client-passwords', { ...data, clientId }),
  update: (clientId, id, data) => apiClient.put(`/client-passwords/${id}`, data),
  delete: (clientId, id) => apiClient.delete(`/client-passwords/${id}`),
};

// Company Settings API
export const companyAPI = {
  getSettings: () => apiClient.get('/company/settings'),
  updateSettings: (data) => apiClient.put('/company/settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeLogo: () => apiClient.delete('/company/logo'),
};

// Lookup Values API (Industries, Referral Sources)
export const lookupValuesAPI = {
  getAll: () => apiClient.get('/lookup-values'),
  update: (data) => apiClient.put('/lookup-values', data),
  addIndustry: (value) => apiClient.post('/lookup-values/industries', null, { params: { value } }),
  deleteIndustry: (value) => apiClient.delete(`/lookup-values/industries/${encodeURIComponent(value)}`),
  addReferralSource: (value) => apiClient.post('/lookup-values/referral-sources', null, { params: { value } }),
  deleteReferralSource: (value) => apiClient.delete(`/lookup-values/referral-sources/${encodeURIComponent(value)}`),
};

// Production Report API
export const productionReportAPI = {
  getReport: (data) => apiClient.post('/reports/production', data),
  getFilterOptions: () => apiClient.get('/reports/filter-options'),
};

export default apiClient;
